import React, { useEffect, useState } from 'react';
import { Box, Divider, Grid, Typography } from '@mui/material';
import { useLazyDownloadInvoiceSignatureQuery } from '../../../store/slices/apiSlice';

const PAYMENT_TERM_LABELS = {
  IMMEDIATE: 'Immediate',
  SEVEN_DAYS: '7 Days',
  TWENTY_DAYS: '20 Days',
  THIRTY_DAYS: '30 Days',
  SIXTY_DAYS: '60 Days',
  NINETY_DAYS: '90 Days',
};

const base64ToArrayBuffer = (base64) => {
  const binaryString = window.atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

function InvoiceSignatureReadonly({ invoiceId, s3Key }) {
  const [sign, setSign] = useState(null);
  const [downloadInvoiceSignature] = useLazyDownloadInvoiceSignatureQuery();

  useEffect(() => {
    if (!s3Key || !invoiceId) {
      setSign(null);
      return;
    }

    (async () => {
      const result = await downloadInvoiceSignature({ invoiceId });
      if (result.data) {
        const byteArray = base64ToArrayBuffer(result.data);
        const blob = new Blob([byteArray], { type: 'application/octet-stream' });
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => setSign(reader.result);
      }
    })();
  }, [s3Key, invoiceId, downloadInvoiceSignature]);

  if (!s3Key) return null;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        height: 60,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'grey.50',
      }}
    >
      {sign ? (
        <img src={sign} height={50} alt="Authoritative signature" />
      ) : (
        <Typography variant="caption" color="text.secondary">
          Signature on file
        </Typography>
      )}
    </Box>
  );
}

const ReadonlyApprovalDetails = ({ invoiceDetails, invoiceId }) => {
  if (!invoiceDetails) return null;

  const { paymentTerm, comment, authoritativeName, authoritativeSignature } = invoiceDetails;
  const hasPaymentTerm = Boolean(paymentTerm);
  const hasComment = Boolean(comment?.trim());
  const hasName = Boolean(authoritativeName?.trim());
  const hasSignature = Boolean(authoritativeSignature);

  if (!hasPaymentTerm && !hasComment && !hasName && !hasSignature) {
    return null;
  }

  return (
    <>
      <Grid item xs={12}>
        <Divider sx={{ my: 1 }} />
        <Typography variant="h6" color="initial" sx={{ fontWeight: 600, mb: 1 }}>
          Approval Details
        </Typography>
      </Grid>
      {hasPaymentTerm && (
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
            Payment Term
          </Typography>
          <Typography variant="body1">{PAYMENT_TERM_LABELS[paymentTerm] || paymentTerm}</Typography>
        </Grid>
      )}
      {hasComment && (
        <Grid item xs={12} sm={hasPaymentTerm ? 8 : 12}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
            Comment
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {comment}
          </Typography>
        </Grid>
      )}
      {hasName && (
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
            Authoritative Name
          </Typography>
          <Typography variant="body1">{authoritativeName}</Typography>
        </Grid>
      )}
      {hasSignature && (
        <Grid item xs={12} sm={hasName ? 8 : 12}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
            Authoritative Signature
          </Typography>
          <InvoiceSignatureReadonly invoiceId={invoiceId} s3Key={authoritativeSignature} />
        </Grid>
      )}
      <Grid item xs={12}>
        <Divider sx={{ my: 1 }} />
      </Grid>
    </>
  );
};

export default ReadonlyApprovalDetails;
